import { formatCurrency, formatGrossProfitDisplayValue } from '@/utils/money';
import {
  LoadSalesPortalOrderSummaryQueryResult,
  useLoadSalesPortalOrderSummaryLazyQuery,
} from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/documents/load-sales-portal-order-summary.generated';
import { useCallback, useMemo, useState } from 'react';

export interface OrderEntryFeeData {
  amountDisplayString: string;
  name: string;
}

export interface OrderEntryOrderFeesData {
  discount: OrderEntryFeeData | null;
  orderPromotionDiscount: OrderEntryFeeData | null;
  promotionDiscount: OrderEntryFeeData | null;
  subtotal: OrderEntryFeeData | null;
  tax: OrderEntryFeeData | null;
  tip: OrderEntryFeeData | null;
  total: OrderEntryFeeData | null;
  volumeDiscount?: OrderEntryFeeData | null;
}

export interface OrderEntrySplitMetricsData {
  fulfillmentDate: string | null;
  fulfillmentType: string;
  grossProfitInMicros: number | null;
  grossProfitInPercentage: number | null;
  grossProfitPercentageDisplayString: string;
  itemCount: number;
  subtotalDisplayString: string;
  subtotalInMicros: number | null;
  unitCount: number;
}

export interface OrderEntrySummaryData {
  currency: string;
  orderFees: OrderEntryOrderFeesData;
  orderId: string;
  splitOrderMetrics: OrderEntrySplitMetricsData[];
}

const feeTransformer = (
  fee: { amount_in_micros: number | null; name: string } | null | undefined,
  currency: string,
): OrderEntryFeeData | null => {
  if (!fee || fee.amount_in_micros === null) {
    return null;
  }
  return {
    amountDisplayString: formatCurrency(fee.amount_in_micros, currency),
    name: fee.name,
  };
};

const orderFeeTransformer = (
  currency: string,
  orderFees: NonNullable<
    LoadSalesPortalOrderSummaryQueryResult['data']
  >['getOrderEntrySummary']['summaries'][0]['order_fees'],
) => {
  const transformedOrderFees: OrderEntryOrderFeesData = {
    discount: feeTransformer(orderFees.discount, currency),
    orderPromotionDiscount: feeTransformer(
      orderFees.order_promotion_discount,
      currency,
    ),
    promotionDiscount: feeTransformer(orderFees.promotion_discount, currency),
    subtotal: feeTransformer(orderFees.subtotal, currency),
    tax: feeTransformer(orderFees.tax, currency),
    tip: feeTransformer(orderFees.tip, currency),
    total: feeTransformer(orderFees.total, currency),
    volumeDiscount: feeTransformer(orderFees.volume_discount, currency),
  };

  return transformedOrderFees;
};

interface UseSalesPortalOrderSummaryLoaderOptions {
  /** When true, GP fields are nulled out in the returned metrics */
  isGrossProfitHidden?: boolean;
}

export const useSalesPortalOrderSummaryLoader = (
  options?: UseSalesPortalOrderSummaryLoaderOptions,
) => {
  const isGrossProfitHidden = options?.isGrossProfitHidden ?? false;

  const [loadSalesPortalOrderSummaryLazyQuery, { data, error, loading }] =
    useLoadSalesPortalOrderSummaryLazyQuery({
      fetchPolicy: 'network-only',
    });
  const [cleared, setCleared] = useState(false);

  // Transform query data into the shape we need to render,
  // and memoize it so that components have a stable reference
  const summaries: OrderEntrySummaryData[] | null = useMemo(() => {
    if (!data?.getOrderEntrySummary?.summaries) {
      return null;
    }

    return data.getOrderEntrySummary.summaries.map((summary) => {
      const currency = summary.currency;
      const orderFees = summary.order_fees;
      const splitOrderMetrics = summary.split_order_metrics ?? [];

      // Transform order fees
      const transformedOrderFees: OrderEntryOrderFeesData = orderFeeTransformer(
        currency,
        orderFees,
      );

      // Transform split order metrics, filtering GP fields when hidden
      const transformedSplitMetrics: OrderEntrySplitMetricsData[] =
        splitOrderMetrics.map((metric) => {
          const gp = metric.gross_profit_in_percentage ?? null;
          const grossProfitPercentageDisplayString = gp
            ? `${formatGrossProfitDisplayValue(gp)}%`
            : '';
          const subtotalInMicros = metric.subtotal_in_micros ?? null;

          return {
            fulfillmentDate: metric.fulfillment_date ?? null,
            fulfillmentType: metric.fulfillment_type,
            grossProfitInMicros: isGrossProfitHidden
              ? null
              : (metric.gross_profit_in_micros ?? null),
            grossProfitInPercentage: isGrossProfitHidden ? null : gp,
            grossProfitPercentageDisplayString: isGrossProfitHidden
              ? ''
              : grossProfitPercentageDisplayString,
            itemCount: metric.item_count,
            subtotalDisplayString:
              subtotalInMicros === null
                ? ''
                : formatCurrency(subtotalInMicros, currency),
            subtotalInMicros,
            unitCount: metric.unit_count,
          };
        });

      return {
        currency,
        orderFees: transformedOrderFees,
        orderId: summary.order_id,
        splitOrderMetrics: transformedSplitMetrics,
      };
    });
  }, [data, isGrossProfitHidden]);

  const reset = useCallback(() => {
    setCleared(true);
  }, []);

  const loadOrderSummary: typeof loadSalesPortalOrderSummaryLazyQuery =
    useCallback(
      (...args) => {
        setCleared(false);
        return loadSalesPortalOrderSummaryLazyQuery(...args);
      },
      [loadSalesPortalOrderSummaryLazyQuery],
    );

  return {
    data: cleared ? null : summaries,
    error: cleared ? undefined : error,
    loading,
    loadOrderSummary,
    reset,
  };
};
